<template>
  <div class="healthcheck-logs-container">
    <div>
      <div class="d-flex justify-content-end">
        <!-- check for mobile screens the multinested filter -->
        <flat-pickr
          v-model="selectedDate"
          placeholder="Select date"
          class="w-25 mr-1 border rounded text-center"
          :config="dateRangeConfigs"
          @on-close="applyCustomDate"
        />

        <b-button
          class="bg-white border"
          variant="none"
          :disabled="healthcheckLogs.length === 0"
          @click="toggleSorting"
        >
          <GjIcon
            :key="buttonText"
            :name="buttonText === 'Ascending' ? 'OrderAsc' : 'OrderDesc'"
          />
          {{ buttonText }}
        </b-button>

        <filters
          class="ml-1"
          :healthcheck-data-centers="healthcheckDataCenters"
          :state-options="stateOptions"
          :comparison-options="comparisonOptions"
          :selected-data-center="dataCenter"
          :selected-state="selectedState"
          :selected-comparison="selectedComparison"
          :selected-response-time="comparisonRange"
          :filter-finished="filterFinished"
          :selected-http-code="statusCode"
          @dataCenterFilterValue="getDataCenterFilterValue"
          @stateFilterValue="getStateFilterValue"
          @filterComparison="getFilterComparison"
          @statusCodeFilter="getStatusCodeFilter"
          @clearComparisonFilter="getClearComparisonFIlter"
          @clearHttpCode="getClearHttpCode"
          @hideDropdowns="getHideDropdowns"
        />
      </div>
      <paper-simple v-if="loading">
        <loading-page />
      </paper-simple>
      <div
        v-else
        class="default-table"
      >
        <b-table
          ref="table"
          :items="healthcheckLogs"
          :fields="fields"
          show-empty
          responsive
          bordered
          :class="{ loaded: !loading }"
          :busy="loading"
        >
          <template #cell(isHealthy)="data">
            <span class="tag">
              <b-badge
                :variant="
                  isHealthy(data.item)
                    ? 'light-success'
                    : 'light-danger'
                "
              >
                {{
                  isHealthy(data.item)
                    ? 'Healthy'
                    : 'Unhealthy'
                }}
              </b-badge>
            </span>
          </template>

          <template #cell(url)="data">
            <div>
              <span class="tag">
                <b-badge :variant="changeMethodBadge(data.item.methodToString)">
                  {{ data.item.methodToString }}
                </b-badge>
              </span>
              <span
                v-if="data.item.url"
                class="ml-1"
              >
                <a
                  v-clipboard:copy="data.item.url"
                  v-clipboard:success="copySuccess"
                  class="healthcheck-url"
                >
                  <span class="p-0 m-0">
                    {{ data.item.url }}
                  </span>
                  <span class="copy-url">
                    <GjIcon name="Copy" />
                  </span>
                </a>
              </span>
              <span v-else>
                URL is null
              </span>
            </div>
          </template>

          <template #cell(statusCode)="data">
            <span v-if="data.item.hasTimedOut || data.item.hasError">
              <h6
                v-if="data.item.hasTimedOut"
                class="log-timeout"
              >Timeout</h6>
              <h6
                v-else
                class="log-timeout"
              >Failed</h6>
            </span>
            <span v-else>
              {{
                data.item.statusCode
                  ? `${data.item.statusCode}: ${data.item.statusCodeToString}`
                  : '-'
              }}
            </span>
          </template>

          <template #cell(responseTime)="data">
            <span v-if="data.item.hasTimedOut || data.item.hasError">
              <h6
                v-if="data.item.hasTimedOut"
                class="log-timeout"
              >Timeout</h6>
              <h6
                v-else
                class="log-timeout"
              >Failed</h6>
            </span>
            <span v-else>
              {{ data.item.responseTime ? `${data.item.responseTime}ms` : '-' }}
            </span>
          </template>

          <template #cell(reason)="data">
            <span>
              {{ returnReason(data.item) }}
            </span>
          </template>

          <template #cell(executedAt)="data">
            <span>
              {{
                data.item.executedAt
                  ? `${timeSince(data.item.executedAt)} ago`
                  : '-'
              }}
            </span>
          </template>

          <template #cell(dataCenterId)="data">
            {{ getDataCenterName(data.item.dataCenterId) }}
          </template>
          <template #cell(actionButton)="data">
            <b-button
              variant="bg-white"
              @click="onDetailsClick(data.item.id)"
            >
              Details
            </b-button>
          </template>

          <template #empty>
            <div
              v-if="loading"
              class="loading-table text-center py-1"
            >
              <b-spinner variant="primary" />
            </div>
            <template v-else>
              <no-data
                no-data-text="No healthcheck logs were found matching your criteria!"
                access-page="Healthchecks"
                icon="Monitoring"
              />
            </template>
          </template>
        </b-table>
        <table-busy v-if="loading && healthcheckLogs.length > 0" />
        <div class="d-flex justify-content-between pt-1 pb-1">
          <div class="results pl-1">
            Viewing {{ getViewingCount() }} to
            {{ getViewingToCount() }} of
            {{ totalCount.toLocaleString() }} Items
          </div>
          <div class="mr-1">
            <b-button
              variant="bg-white"
              :disabled="resultInfo.previousCount <= 0"
              @click="onClickPrevious"
            >
              Previous
            </b-button>
            <b-button
              class="ml-1"
              variant="bg-white"
              :disabled="resultInfo.nextCount <= 0"
              @click="onClickNext"
            >
              Next
            </b-button>
          </div>
        </div>
      </div>
      <healthcheck-log-details
        v-if="showView"
        @close="showView = false"
      />
    </div>
  </div>
</template>

<script>
import HealthcheckLogDetails from './HealthcheckLogDetails.vue'
import Filters from './Filters/Filters.vue'
import FlatPickr from 'vue-flatpickr-component'

export default {
  name: 'HealthcheckLogs',
  components: {
    HealthcheckLogDetails,
    Filters,
    FlatPickr,
  },
  data() {
    return {
      id: null,
      filterFinished: false,
      selectedState: 2,
      statusCode: null,
      dataCenter: 0,
      selectedOrder: 1,
      selectedComparison: null,
      rangeDate: '',
      selectedDate: null,
      isAscending: false,
      selectedSort: 1,
      showForm: false,
      showView: false,
      dateRangeConfigs: {
        mode: 'range',
        enableTime: true,
        dateFormat: 'Y-m-d H:i',
        minDate: '1970-01-01',
        maxDate: new Date().toISOString().split('T')[0],
      },
      fields: [
        { key: 'id', label: 'ID' },
        { key: 'isHealthy', label: 'Health Status' },
        { key: 'url', label: 'URL' },
        { key: 'statusCode', label: 'Status Code' },
        { key: 'responseTime', label: 'Response Time' },
        { key: 'reason', label: 'Reason' },
        { key: 'executedAt', label: 'Last Checked' },
        { key: 'dataCenterId', label: 'Data Center' },
        { key: 'retries', formatter: val => `${val}/${this.healthcheck.retries}` },
        { key: 'actionButton', label: '' },
      ],
      stateOptions: [
        { id: 2, text: 'Any State' },
        { id: 1, text: 'Healthy' },
        { id: 0, text: 'Unhealthy' },
      ],
      orderOptions: [
        { id: 0, text: 'Ascending' },
        { id: 1, text: 'Descending' },
      ],
      comparisonOptions: [
        { id: 0, text: 'Greater Than' },
        { id: 1, text: 'Less Than' },
      ],
      hasHealthcheckLogsPermissions: true,
      pageSize: 10,
      comparisonRange: null,
    }
  },
  computed: {
    buttonText() {
      return this.isAscending ? 'Ascending' : 'Descending'
    },
    customDate() {
      return this.$store.state.healthcheckLogs.customDate
    },
    loading() {
      return this.$store.state.healthcheckLogs.fetchingHealthcheckLogs
    },
    loadingHealthcheckLog() {
      return this.$store.state.healthcheckLogs.gettingHealthcheckLog
    },
    healthcheckLogs() {
      return this.$store.state.healthcheckLogs.healthcheckLogs
    },
    healthcheck() {
      return this.$store.state.healthchecks.healthcheck
    },
    healthcheckDataCenters() {
      return this.$store.state.healthcheckLogs.healthcheckDataCenters
    },
    totalCount() {
      return this.resultInfo?.totalCount
    },
    resultInfo() {
      return this.$store.state.healthcheckLogs.healthcheckLogsResultInfo
    },
    firstId() {
      return this.healthcheckLogs[0]?.id || 0
    },
    lastId() {
      return this.healthcheckLogs[this.healthcheckLogs?.length - 1]?.id || 0
    },
  },
  created() {
    this.id = this.$route.params.healthcheckId
    this.$store.commit('healthcheckLogs/DESTROY_CUSTOM_DATE')
    if (this.healthcheckLogs.length <= 0) {
      if (this.healthcheckDataCenters.length <= 0) {
        this.clearStore()
        this.getHealthcheckDataCenters()
      } else {
        this.generateQueryAndFetchResults()
      }
    }
  },
  methods: {
    applyCustomDate(e) {
      if (e.length === 0) return

      const utcDates = e.map(d => d.toISOString())
      this.$store.commit('healthcheckLogs/SET_CUSTOM_DATE', utcDates)
      this.onFilterClick()
    },
    isHealthy(data) {
      if (data.hasCustomBodyValidation) {
        return data.isCustomBodyMatching
      }

      return data.statusCode === data.expectedResponse
    },
    returnReason(data) {
      if (data.hasCustomBodyValidation) {
        return data.isCustomBodyMatching ? 'Response Body matched' : 'Response Body was not matched'
      }

      return data.statusCode === data.expectedResponse ? 'Status Code matched' : 'Status Code was not matched'
    },
    urlFormatted(protocol, url) {
      return protocol === 0 ? `https://${url || '-'}` : `http://${url || '-'}`
    },
    closeForm() {
      this.showView = false
    },
    changeMethodBadge(method) {
      if (method === 'GET') {
        return 'light-success'
      }
      if (method === 'DELETE') {
        return 'light-danger'
      }
      if (method === 'POST') {
        return 'light-warning'
      }
      if (method === 'PUT') {
        return 'light-primary'
      }
      return 'light-secondary'
    },
    changeState(id) {
      this.selectedState = id
    },
    toggleSorting() {
      this.isAscending = !this.isAscending
      this.selectedSort = this.isAscending ? 0 : 1
      this.onFilterClick()
    },
    getHideDropdowns(v) {
      this.filterFinished = v
    },
    changeOrder(id) {
      this.selectedOrder = id
    },
    changeComparison(id) {
      this.selectedComparison = id
    },
    getHealthcheckLogsResults(query) {
      this.$store
        .dispatch('healthcheckLogs/fetchHealthcheckLogsResults', {
          healthcheckId: this.id,
          query,
        })
        .then(() => {
          this.filterFinished = true
        })
        .catch(error => {
          this.filterFinished = true
          if (error.response?.status === 403) {
            this.hasHealthcheckLogsPermissions = false
            return
          }
          this.errorToast(
            error.response?.data?.errors[0]
              || 'Something went wrong while fetching healthcheck logs results!',
          )
        })
    },
    getHealthcheckLogDetails(healthcheckLogId) {
      this.$store
        .dispatch('healthcheckLogs/getHealthcheckLog', {
          healthcheckId: this.id,
          healthcheckLogId,
        })
        .catch(error => {
          if (error.response?.status === 403) {
            this.hasHealthcheckLogsPermissions = false
            return
          }
          this.errorToast(
            error.response?.data?.errors[0]
              || 'Something went wrong while fetching healthcheck log details!',
          )
        })
    },
    onDetailsClick(healthcheckLogId) {
      this.getHealthcheckLogDetails(healthcheckLogId)
      this.showView = true
    },
    clearStore() {
      this.$store.commit('healthcheckLogs/SET_HEALTHCHECK_DATACENTERS', [])
    },
    getHealthcheckDataCenters() {
      const dataCenters = [{ id: 0, name: 'All', slug: 'Data Centers' }]

      this.healthcheck.healthcheckLogStatuses.forEach(hl => {
        dataCenters.push(hl.dataCenter)
      })

      if (dataCenters.length > 0 && this.healthcheckDataCenters.length <= 0) {
        this.changeDataCenter(dataCenters[0])
        this.generateQueryAndFetchResults()
        this.clearFilteredLogs()
        this.$store.commit('healthcheckLogs/SET_HEALTHCHECK_DATACENTERS', dataCenters)
      }

      return dataCenters
    },
    changeDataCenter(dataCenter) {
      if (dataCenter === this.selectedDataCenter) return
      this.selectedDataCenter = dataCenter
    },
    clearFilteredLogs() {
      this.$store.commit('healthcheckLogs/CLEAR_FILTERED_HEALTHCHECK_LOGS')
    },
    getDataCenterName(dataCenterId) {
      const dataCenter = this.healthcheckDataCenters.find(dc => dc.id === dataCenterId)
      if (dataCenter) {
        return `${dataCenter.name} - ${dataCenter.slug}`
      }
      return ''
    },
    onClickPrevious() {
      return this.generateQueryAndFetchResults(0)
    },
    onClickNext() {
      return this.generateQueryAndFetchResults(1)
    },
    getDataCenterFilterValue(val) {
      this.dataCenter = val
      this.onFilterClick()
    },
    getStateFilterValue(val) {
      this.selectedState = val
      this.onFilterClick()
    },
    getFilterComparison(obj) {
      this.selectedComparison = obj.option
      this.comparisonRange = obj.responseTime
      this.onFilterClick()
    },
    getClearComparisonFIlter(obj) {
      this.selectedComparison = obj.option
      this.comparisonRange = obj.responseTime
      this.onFilterClick()
    },
    getClearHttpCode(val) {
      this.statusCode = val
      this.onFilterClick()
    },
    getStatusCodeFilter(val) {
      this.statusCode = val
      this.onFilterClick()
    },
    generateQueryAndFetchResults(navigation) {
      // eslint-disable-next-line no-nested-ternary
      const paginationId = navigation === 0 ? this.firstId : (navigation === 1 ? this.lastId : 0)

      const query = {
        lastId: paginationId,
        navigationType: navigation,
        pageSize: this.pageSize,
        orderType: this.selectedSort,
        state: this.selectedState,
        dataCenterId: this.dataCenter,
        rangeType: this.selectedComparison,
        range: this.comparisonRange,
        statusCode: parseInt(this.statusCode, 10),
      }

      if (this.customDate.length) {
        // eslint-disable-next-line prefer-destructuring
        query.dateFrom = this.customDate[0]
        // eslint-disable-next-line prefer-destructuring
        query.dateTo = this.customDate[1]
      }

      this.getHealthcheckLogsResults(query)
    },
    onFilterClick() {
      this.generateQueryAndFetchResults()
    },
    getViewingCount() {
      return (this.resultInfo.previousCount === 0 ? this.healthcheckLogs.length : this.resultInfo.previousCount + this.pageSize).toLocaleString()
    },
    getViewingToCount() {
      if (this.resultInfo.nextCount === 0) { return this.resultInfo.totalCount }
      return (this.resultInfo.previousCount === 0 ? this.pageSize : this.resultInfo.previousCount + (this.pageSize * 2)).toLocaleString()
    },
  },
}
</script>
<style lang="scss" scoped>
.tag {
  font-size: 1.5em;
}

.p-25 {
  padding: 4px;
}
.comparison-dropdown::v-deep {
  button{
    border-top-right-radius: 0px !important;
    border-bottom-right-radius: 0px !important;
  }
}
.comnparison-range{
  border-top-left-radius: 0px !important;
  border-bottom-left-radius: 0px !important;
}
.default-table::v-deep {
  .healthcheck-url {
    color: #1d79f2;
    &:hover {
      color: #0d6ce8;
    }
  }
  table {
    margin-top: 0;
  }
  td {
    padding: 5px;
    border-bottom: 1px solid #e0e5eb;
  }
  .log-timeout {
    color: #e34850;
  }
}
</style>
